import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useI18n, useTranslate } from '../contexts/I18nContext';
import { useCurrency } from '../contexts/CurrencyContext';
import { SEO } from '../components/SEO';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import { DEFAULT_EVENT_IMAGE, handleEventImageError } from '../utils/imageUrl';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { format } from 'date-fns';
import { es, enUS, pt } from 'date-fns/locale';
import { formatChile, toChileDate } from '../utils/dateUtils';
import { isPriceUnknown, PRICE_TBC_LABEL } from '../utils/price';
import {
  MapPin, Calendar, Clock, Star, CheckCircle, 
  Globe, Phone, ArrowLeft, ExternalLink, Users,
  Ticket, Music, ChevronRight, Sparkles
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const ProviderProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, locale } = useI18n();
  const { translateText } = useTranslate();
  const { formatPrice, currency } = useCurrency();

  const [provider, setProvider] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const dateLocales = { es, en: enUS, pt };

  useEffect(() => {
    fetchProviderProfile();
  }, [id]);

  const fetchProviderProfile = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/providers/${id}/public`);
      setProvider(response.data.provider);
      setEvents(response.data.events);
    } catch (err) {
      console.error('Failed to fetch provider:', err);
      setError('No se encontró el proveedor');
    } finally {
      setLoading(false);
    }
  };

  const formatEventDate = (startDate, endDate) => {
    if (!startDate) return '';
    const start = toChileDate(startDate);
    
    if (endDate) {
      const end = toChileDate(endDate);
      if (start.toDateString() === end.toDateString()) {
        return formatChile(startDate, "EEEE d 'de' MMMM, yyyy", locale);
      }
      return `${formatChile(startDate, "d MMM", locale)} - ${formatChile(endDate, "d MMM yyyy", locale)}`;
    }
    return formatChile(startDate, "EEEE d 'de' MMMM, yyyy", locale);
  };

  const groupEventsByMonth = (events) => {
    const groups = {};
    events.forEach(event => {
      if (event.start_date) {
        const key = formatChile(event.start_date, 'MMMM yyyy', locale);
        if (!groups[key]) groups[key] = [];
        groups[key].push(event);
      }
    });
    return groups;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAF9]">
        <div className="bg-[#064E3B] text-white py-16 px-4">
          <div className="max-w-4xl mx-auto">
            <Skeleton className="h-8 w-48 bg-green-800 mb-4" />
            <Skeleton className="h-12 w-96 bg-green-800 mb-2" />
            <Skeleton className="h-6 w-64 bg-green-800" />
          </div>
        </div>
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="space-y-4">
            {[1, 2, 3].map(i => <Skeleton key={i} className="h-32 rounded-2xl" />)}
          </div>
        </div>
      </div>
    );
  }

  if (error || !provider) {
    return (
      <div className="min-h-screen bg-[#F8FAF9] flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-500 mb-4">{error || translateText('Proveedor no encontrado')}</p>
          <Button onClick={() => navigate(-1)} variant="outline">
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t('back')}
          </Button>
        </div>
      </div>
    );
  }

  const eventsByMonth = groupEventsByMonth(events);

  return (
    <div className="min-h-screen bg-[#F8FAF9]" data-testid="provider-profile">
      <SEO
        title={`${provider.business_name} - Organizador de Eventos`}
        description={provider.description?.substring(0, 160) || `${provider.business_name} - Organizador de panoramas y eventos en PlanCity. Descubre sus proximos eventos.`}
        path={`/artist/${provider.id}`}
        image={provider.logo_url}
      />
      {/* Hero Header */}
      <div className="bg-gradient-to-br from-[#064E3B] to-[#0D7C5C] text-white py-12 px-4">
        <div className="max-w-4xl mx-auto">
          <Button 
            variant="ghost" 
            className="text-white/80 hover:text-white hover:bg-white/10 mb-6 -ml-2"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t('back')}
          </Button>

          <div className="flex flex-col md:flex-row gap-6 items-start">
            {/* Logo/Avatar */}
            <div className="w-24 h-24 md:w-32 md:h-32 bg-white/20 rounded-2xl flex items-center justify-center overflow-hidden shrink-0">
              {provider.logo_url ? (
                <img src={provider.logo_url} alt={provider.business_name} className="w-full h-full object-cover" />
              ) : (
                <Music className="w-12 h-12 md:w-16 md:h-16 text-white/60" />
              )}
            </div>

            {/* Info */}
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2 flex-wrap">
                <h1 className="text-3xl md:text-4xl font-bold">{translateText(provider.business_name)}</h1>
                {provider.is_verified && (
                  <Badge className="bg-blue-500 text-white border-0">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    {t('verified')}
                  </Badge>
                )}
                {provider.is_active_organizer && (
                  <Badge className="bg-purple-500 text-white border-0">
                    <Sparkles className="w-3 h-3 mr-1" />
                    {t('activeOrganizer')}
                  </Badge>
                )}
              </div>

              {provider.description && (
                <p className="text-white/80 mb-4 max-w-2xl">{translateText(provider.description)}</p>
              )}

              {/* Stats */}
              <div className="flex flex-wrap gap-4 text-sm">
                <div className="flex items-center gap-2 bg-white/10 px-3 py-1.5 rounded-full">
                  <Calendar className="w-4 h-4" />
                  <span>{provider.active_events_count} {translateText('eventos activos')}</span>
                </div>
                {provider.average_rating && (
                  <div className="flex items-center gap-2 bg-white/10 px-3 py-1.5 rounded-full">
                    <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                    <span>{provider.average_rating} ({provider.total_ratings} {translateText('reseñas')})</span>
                  </div>
                )}
                {provider.website && (
                  <a 
                    href={provider.website} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 bg-white/10 px-3 py-1.5 rounded-full hover:bg-white/20 transition-colors"
                  >
                    <Globe className="w-4 h-4" />
                    <span>{translateText('Sitio web')}</span>
                    <ExternalLink className="w-3 h-3" />
                  </a>
                )}
                {provider.phone && (
                  <a 
                    href={`tel:${provider.phone}`}
                    className="flex items-center gap-2 bg-white/10 px-3 py-1.5 rounded-full hover:bg-white/20 transition-colors"
                  >
                    <Phone className="w-4 h-4" />
                    <span>{provider.phone}</span>
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Events List */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <h2 className="text-2xl font-bold text-[#064E3B] mb-6 flex items-center gap-2">
          <Ticket className="w-6 h-6" />
          {translateText('Próximos Eventos')}
          <Badge variant="outline" className="ml-2">{events.length}</Badge>
        </h2>

        {events.length === 0 ? (
          <Card className="rounded-2xl border-0 shadow-sm">
            <CardContent className="py-12 text-center">
              <Calendar className="w-12 h-12 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500">{translateText('No hay eventos activos en este momento')}</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-10">
            {Object.entries(eventsByMonth).map(([month, monthEvents]) => (
              <div key={month}>
                <h3 className="text-lg font-semibold text-gray-600 mb-6 capitalize sticky top-20 bg-[#F8FAF9] py-2 z-10">
                  {month}
                </h3>
                <div className="flex flex-col gap-8">
                  {monthEvents.map((event) => (
                    <Link 
                      to={`/event/${event.id}`} 
                      key={event.id}
                      className="block"
                    >
                      <Card className="rounded-2xl border border-gray-100 shadow-sm hover:shadow-lg transition-all group overflow-hidden">
                        <CardContent className="p-0">
                          <div className="flex flex-col md:flex-row">
                            {/* Image */}
                            <div className="md:w-48 h-40 shrink-0 relative">
                              {event.main_image ? (
                                <img 
                                  src={event.main_image} 
                                  alt={event.name}
                                  className="w-full h-full object-cover"
                                  style={{ objectPosition: `50% ${event.image_position || '50%'}` }}
                                  onError={handleEventImageError}
                                />
                              ) : (
                                <img 
                                  src={DEFAULT_EVENT_IMAGE} 
                                  alt={event.name}
                                  className="w-full h-full object-cover"
                                />
                              )}
                              {/* Provider avatar overlay */}
                              {provider.logo_url && (
                                <div className="absolute bottom-2 left-2 w-10 h-10 rounded-full border-2 border-white shadow-md overflow-hidden bg-white">
                                  <img 
                                    src={provider.logo_url} 
                                    alt={provider.business_name}
                                    className="w-full h-full object-cover"
                                  />
                                </div>
                              )}
                            </div>
                            
                            {/* Content */}
                            <div className="flex-1 p-5">
                              <div className="flex items-start justify-between gap-4">
                                <div className="flex-1 min-w-0">
                                  {/* Category Badge */}
                                  {event.category_name && (
                                    <Badge 
                                      className="mb-2 border-0 rounded-full"
                                      style={{ 
                                        backgroundColor: `${event.category_color}20`,
                                        color: event.category_color 
                                      }}
                                    >
                                      {translateText(event.category_name)}
                                    </Badge>
                                  )}
                                  
                                  <h4 className="text-lg font-bold text-[#064E3B] group-hover:text-[#00CC66] transition-colors mb-2">
                                    {translateText(event.name)}
                                  </h4>
                                  
                                  {event.short_description && (
                                    <p className="text-gray-500 text-sm mb-3 line-clamp-2">
                                      {translateText(event.short_description)}
                                    </p>
                                  )}
                                  
                                  {/* Event Details */}
                                  <div className="flex flex-wrap gap-x-4 gap-y-2 text-sm text-gray-600">
                                    <div className="flex items-center gap-1.5">
                                      <Calendar className="w-4 h-4 text-[#00CC66]" />
                                      <span>{formatEventDate(event.start_date, event.end_date)}</span>
                                    </div>
                                    
                                    {event.schedule && (
                                      <div className="flex items-center gap-1.5">
                                        <Clock className="w-4 h-4 text-[#00CC66]" />
                                        <span>{event.schedule}</span>
                                      </div>
                                    )}
                                    
                                    {(event.commune_name || event.region_name) && (
                                      <div className="flex items-center gap-1.5">
                                        <MapPin className="w-4 h-4 text-[#00CC66]" />
                                        <span>
                                          {event.commune_name}
                                          {event.region_name && `, ${event.region_name}`}
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                                
                                {/* Price & Action */}
                                <div className="text-right shrink-0">
                                  <div className="mb-2">
                                    {event.is_free ? (
                                      <Badge className="bg-green-100 text-green-700 border-0 rounded-full">
                                        {t('freeEvent')}
                                      </Badge>
                                    ) : isPriceUnknown(event) ? (
                                      <span className="text-sm font-semibold text-gray-500">{PRICE_TBC_LABEL}</span>
                                    ) : (
                                      <span className="text-xl font-bold text-[#064E3B]">
                                        {formatPrice(event.price)}
                                      </span>
                                    )}
                                  </div>
                                  
                                  {event.average_rating && (
                                    <div className="flex items-center gap-1 text-sm text-gray-500 justify-end">
                                      <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                                      <span>{event.average_rating}</span>
                                    </div>
                                  )}
                                  
                                  <ChevronRight className="w-5 h-5 text-gray-300 group-hover:text-[#00CC66] transition-colors mt-2 ml-auto" />
                                </div>
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};
